import { monthLabel } from 'components/invoiceImport/invoiceImportText';
import {
  REMINDER_LEVELS,
  countText,
  formatDayMonth,
  formatMomentDayMonth,
  formatMomentFullDay,
  formatReminderEuros,
} from './invoiceRemindersText';
import ReminderBadges from './ReminderBadges';
import ReminderRowMenu from './ReminderRowMenu';

// Left to pay, else the whole amount while Pennylane has not said: what the api totals.
const owedOf = (item) => item.remainingAmount ?? item.amountInclTax;

// "Relance 2/3 le 29/07", the last reminder that went (JBWEB-1284).
const lastSentText = (item, text) => {
  const { lastSent } = item;
  const day = formatMomentDayMonth(lastSent?.at);
  if (!lastSent || !REMINDER_LEVELS.includes(lastSent.level) || !day) {
    return text('litigation.noneSent');
  }
  return text('litigation.lastSent', {
    label: text(`badges.${lastSent.level}`),
    date: day,
  });
};

const LateCell = ({ item, tab, text }) => {
  const dueDay = formatDayMonth(item.dueDate);
  const isLate = item.lateDays > 0;
  let secondLine = null;
  if (tab === 'litigation') {
    // Handed over to collection: what matters is how far the reminders went.
    secondLine = lastSentText(item, text);
  } else if (dueDay) {
    secondLine = isLate
      ? text('row.lateSince', { date: dueDay })
      : text('row.dueOn', { date: dueDay });
  }
  return (
    <>
      <div className="text-gray-900">
        {isLate
          ? countText(text, 'row.lateDays', item.lateDays, {
              days: item.lateDays,
            })
          : text('row.notLate')}
      </div>
      {secondLine && <div>{secondLine}</div>}
    </>
  );
};

const PassageCell = ({ item, text }) => {
  const day = formatMomentFullDay(item.litigation?.since);
  // The api gives the author as an id only: no name to show.
  return day ? text('litigation.since', { date: day }) : null;
};

/**
 * One invoice of the reminders (JBWEB-1283, component Relance/Ligne): number,
 * company and month, left to pay, days late since the due date, its badges,
 * "Relancer" and the "…" (suspend or resume, and in "À faire" move a late
 * invoice to litigation). A click on the row opens the invoice's detail
 * (JBWEB-1274). An invoice no longer reminded (contentious, settled) has
 * "Ouvrir" instead; in the "Contentieux" tab (JBWEB-1284) its days late come
 * with the last reminder sent, a "Passage" column says since when, and the
 * "…" takes it out of litigation.
 */
const ReminderRow = ({
  item,
  tab,
  text,
  onOpen,
  onRemind,
  onToggleSuspension,
  isSuspensionPending,
  onLitigation,
}) => {
  const isReminded = !item.litigation && tab !== 'settled';
  const isInLitigationTab = tab === 'litigation' && Boolean(item.litigation);
  // The api refuses an invoice not late yet (INVOICE_NOT_LATE).
  const canStartLitigation = isReminded && tab === 'todo' && item.lateDays > 0;
  const nextDay =
    !item.dueStep && !item.exhausted && item.nextStep
      ? formatDayMonth(item.nextStep.date)
      : null;
  const stop = (handler) => (event) => {
    event.stopPropagation();
    handler();
  };

  return (
    <tr
      className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
      onClick={() => onOpen(item.number)}
      data-testid={`reminder-row-${item.number}`}
    >
      <td className="px-4 py-3 whitespace-nowrap font-medium text-gray-900">
        {item.number}
      </td>
      <td className="px-2 py-3">
        <div className="font-medium text-gray-900">
          {item.companyName || text('row.noCompany')}
        </div>
        {item.period && (
          <div>{text('row.invoiceOf', { month: monthLabel(item.period) })}</div>
        )}
      </td>
      <td className="px-2 py-3 whitespace-nowrap">
        {formatReminderEuros(owedOf(item))}
      </td>
      <td className="px-2 py-3 whitespace-nowrap">
        <LateCell item={item} tab={tab} text={text} />
      </td>
      {tab === 'litigation' && (
        <td className="px-2 py-3 whitespace-nowrap">
          <PassageCell item={item} text={text} />
        </td>
      )}
      <td className="px-2 py-3">
        <ReminderBadges item={item} tab={tab} text={text} />
        {nextDay && (
          <div className="mt-1 text-xs">
            {text('row.nextOn', { date: nextDay })}
          </div>
        )}
      </td>
      <td className="py-3 pl-2 pr-3">
        <div className="flex justify-end items-center gap-1">
          {isReminded ? (
            <button
              type="button"
              onClick={stop(() => onRemind(item))}
              className="h-9 px-3 rounded-md border border-gray-200 bg-white text-sm text-gray-700 hover:bg-gray-100"
            >
              {text('row.remind')}
            </button>
          ) : (
            <button
              type="button"
              onClick={stop(() => onOpen(item.number))}
              className="h-9 px-3 rounded-md border border-gray-200 bg-white text-sm text-gray-700 hover:bg-gray-100"
            >
              {text('row.open')}
            </button>
          )}
          {isReminded && (
            <ReminderRowMenu
              label={text('row.moreActions', { number: item.number })}
              items={[
                canStartLitigation && {
                  label: text('litigation.start'),
                  onClick: () => onLitigation(item, 'start'),
                },
                {
                  label: item.suspended
                    ? text('row.resume')
                    : text('row.suspend'),
                  disabled: isSuspensionPending,
                  onClick: () => onToggleSuspension(item),
                },
              ].filter(Boolean)}
            />
          )}
          {isInLitigationTab && (
            <ReminderRowMenu
              label={text('row.moreActions', { number: item.number })}
              items={[
                {
                  label: text('litigation.end'),
                  onClick: () => onLitigation(item, 'end'),
                },
              ]}
            />
          )}
          {!isReminded && !isInLitigationTab && <span className="w-9" />}
        </div>
      </td>
    </tr>
  );
};

export default ReminderRow;
